import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Package, ArrowLeft, ArrowRight, User, MapPin, Box,
  CreditCard, CheckCircle, Truck, Shield, Scale, AlertTriangle,
  Phone, Mail, Building2, FileText, DollarSign, Copy, Search,
  Wallet, BookOpen, RefreshCw
} from "lucide-react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { CARRIER_LOGOS } from "@/components/CarrierLogos";
import TrackingTimeline from "@/components/TrackingTimeline";
import ColoniaSearch from "@/components/ColoniaSearch";

const ESTADOS_MEXICO = [
  "Aguascalientes", "Baja California", "Baja California Sur", "Campeche",
  "Chiapas", "Chihuahua", "Ciudad de México", "Coahuila", "Colima",
  "Durango", "Estado de México", "Guanajuato", "Guerrero", "Hidalgo",
  "Jalisco", "Michoacán", "Morelos", "Nayarit", "Nuevo León", "Oaxaca",
  "Puebla", "Querétaro", "Quintana Roo", "San Luis Potosí", "Sinaloa",
  "Sonora", "Tabasco", "Tamaulipas", "Tlaxcala", "Veracruz", "Yucatán",
  "Zacatecas"
];

const CODIGOS_SAT = [
  { code: "50171832", desc: "Otros productos (General)" },
  { code: "53131601", desc: "Ropa y prendas de vestir" },
  { code: "53111601", desc: "Telas y textiles" },
  { code: "53121601", desc: "Hilos y fibras textiles" },
  { code: "46181500", desc: "Calzado" },
  { code: "43211500", desc: "Computadoras y laptops" },
  { code: "43191500", desc: "Teléfonos celulares y smartphones" },
  { code: "52161500", desc: "Televisores y monitores" },
  { code: "43201500", desc: "Accesorios de computación" },
  { code: "43191600", desc: "Accesorios para teléfonos" },
  { code: "53121700", desc: "Accesorios de moda y bisutería" },
  { code: "53131500", desc: "Ropa interior" },
  { code: "42171900", desc: "Productos de belleza y cosméticos" },
  { code: "42142200", desc: "Productos de cuidado personal" },
  { code: "60131000", desc: "Chocolates y confitería" },
  { code: "50202300", desc: "Alimentos procesados" },
  { code: "60121000", desc: "Bebidas" },
  { code: "44121600", desc: "Papelería y artículos de oficina" },
  { code: "55121500", desc: "Artículos de cocina" },
  { code: "56101500", desc: "Muebles para hogar" },
  { code: "52141500", desc: "Equipos de audio y bocinas" },
  { code: "49241700", desc: "Juguetes" },
  { code: "60141000", desc: "Suplementos y vitaminas" },
  { code: "42172000", desc: "Productos farmacéuticos" },
  { code: "44111500", desc: "Libros y publicaciones" },
  { code: "53102500", desc: "Bolsos, maletas y equipaje" },
  { code: "26111700", desc: "Cables y conectores" },
  { code: "40142000", desc: "Artículos deportivos" },
  { code: "14111500", desc: "Documentos y efectos personales" },
];

const STEPS = [
  { id: "remitente", label: "Remitente", icon: User },
  { id: "destinatario", label: "Destinatario", icon: MapPin },
  { id: "paquete", label: "Paquete", icon: Box },
  { id: "pago", label: "Pago", icon: CreditCard },
  { id: "exito", label: "Confirmación", icon: CheckCircle },
];

const ShippingFlow = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, updateBalance } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("balance");

  const quote = location.state?.quote;
  const quoteDetails = location.state?.quoteDetails;

  const [sender, setSender] = useState({
    name: user?.name || "", company: "", street: "", colonia: "",
    city: "", state: "", zip: quoteDetails?.origin_zip || "", phone: "", email: user?.email || ""
  });

  const [recipient, setRecipient] = useState({
    name: "", company: "", street: "", colonia: "",
    city: "", state: "", zip: quoteDetails?.destination_zip || "", phone: "", email: "",
    references: ""
  });

  const [pkg, setPkg] = useState({
    content_description: "", declared_value: "",
    weight: quoteDetails?.weight || "", length: "", width: "", height: "",
    is_fragile: false, codigo_sat: "50171832", codigo_sat_desc: "Otros productos (General)"
  });

  useEffect(() => {
    if (!quote || !quoteDetails) {
      navigate("/dashboard");
    }
    if (quoteDetails?.dimensions) {
      const match = quoteDetails.dimensions.match(/(\d+\.?\d*)x(\d+\.?\d*)x(\d+\.?\d*)/);
      if (match) {
        setPkg(p => ({ ...p, length: match[1], width: match[2], height: match[3] }));
      }
    }
  }, []);

  if (!quote) return null;

  const validateStep = () => {
    if (currentStep === 0) {
      if (!sender.name || !sender.street || !sender.colonia || !sender.city || !sender.state || !sender.zip || !sender.phone || !sender.email) {
        toast.error("Completa todos los campos obligatorios del remitente");
        return false;
      }
    }
    if (currentStep === 1) {
      if (!recipient.name || !recipient.street || !recipient.colonia || !recipient.city || !recipient.state || !recipient.zip || !recipient.phone || !recipient.email) {
        toast.error("Completa todos los campos obligatorios del destinatario");
        return false;
      }
    }
    if (currentStep === 2) {
      if (!pkg.content_description || !pkg.weight || !pkg.length || !pkg.width || !pkg.height) {
        toast.error("Completa la descripción y dimensiones del paquete");
        return false;
      }
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep()) setCurrentStep(s => Math.min(s + 1, STEPS.length - 1));
  };

  const prevStep = () => setCurrentStep(s => Math.max(s - 1, 0));

  const handlePayment = async () => {
    if (user.balance < quote.price) {
      toast.error("Saldo insuficiente. Recarga tu saldo primero.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/shipments/create`, {
        carrier: quote.carrier,
        service_type: quote.service_type,
        price: quote.price,
        delivery_days: quote.delivery_days,
        sender_name: sender.name, sender_company: sender.company,
        sender_street: sender.street, sender_colonia: sender.colonia,
        sender_address_line3: sender.address_line3 || "",
        sender_city: sender.city, sender_state: sender.state,
        sender_zip: sender.zip, sender_phone: sender.phone, sender_email: sender.email,
        recipient_name: recipient.name, recipient_company: recipient.company,
        recipient_street: recipient.street, recipient_colonia: recipient.colonia,
        recipient_address_line3: recipient.address_line3 || "",
        recipient_city: recipient.city, recipient_state: recipient.state,
        recipient_zip: recipient.zip, recipient_phone: recipient.phone, recipient_email: recipient.email,
        recipient_references: recipient.references || recipient.address_line3 || "",
        weight: parseFloat(pkg.weight), length: parseFloat(pkg.length),
        width: parseFloat(pkg.width), height: parseFloat(pkg.height),
        content_description: pkg.content_description,
        declared_value: parseFloat(pkg.declared_value || 0),
        is_fragile: pkg.is_fragile,
        codigo_sat: pkg.codigo_sat,
        // EnviaTuPaquete service data for real label generation
        etp_servicio_id: quote.etp_servicio_id || "",
        etp_paqueteria: quote.etp_paqueteria || "",
        etp_costo: quote.etp_costo || 0,
        etp_costo_proveedor: quote.etp_costo_proveedor || 0,
        etp_fuente: quote.etp_fuente || "",
        etp_servicio_nombre: quote.etp_servicio_nombre || "",
        etp_servicio_codigo: quote.etp_servicio_codigo || "",
        etp_currier_code: quote.etp_currier_code || "",
        etp_service_code: quote.etp_service_code || "",
        etp_quote_id: quote.etp_quote_id || "",
        etp_shipping_code: quote.etp_shipping_code || "",
      });

      const data = res.data;
      setResult(data);
      updateBalance(user.balance - quote.price);
      setCurrentStep(4);

      // If scraping is in progress, start polling for status
      if (data.scraping_in_progress && data.shipment_id) {
        toast.info("Generando guía FedEx automáticamente. Esto puede tomar 1-2 minutos...");
        pollScrapingStatus(data.shipment_id);
      } else {
        toast.success("¡Guía generada exitosamente!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al procesar el envío");
    } finally {
      setLoading(false);
    }
  };

  const pollScrapingStatus = (shipmentId) => {
    let attempts = 0;
    const maxAttempts = 90; // 7.5 minutes at 5s intervals (backend has 7min timeout)
    const interval = setInterval(async () => {
      attempts++;
      try {
        const res = await axios.get(`${API}/shipments/${shipmentId}/scraping-status`);
        const data = res.data;
        console.log(`[FedEx Poll #${attempts}] Status:`, data.status, data.tracking_number);

        if (data.status && data.status.includes("generada")) {
          clearInterval(interval);
          setResult(prev => ({
            ...prev,
            tracking_number: data.tracking_number,
            label_url: data.label_url,
            status: data.status,
            scraping_in_progress: false,
          }));
          toast.success("¡Guía FedEx generada exitosamente!");
        } else if (data.status === "Error") {
          clearInterval(interval);
          setResult(prev => ({
            ...prev,
            status: "Error",
            status_detail: data.status_detail || "Error al generar la guía",
            scraping_in_progress: false,
          }));
          toast.error(data.status_detail || "Error al generar la guía FedEx");
        } else {
          // Still processing - update the detail text
          setResult(prev => ({
            ...prev,
            status_detail: data.status_detail || "Procesando...",
          }));
        }
      } catch (err) {
        console.warn(`[FedEx Poll #${attempts}] Error:`, err.message);
      }

      if (attempts >= maxAttempts) {
        clearInterval(interval);
        setResult(prev => ({
          ...prev,
          scraping_in_progress: false,
          status: "Error",
          status_detail: "Tiempo agotado. Revisa tu historial de envíos.",
        }));
      }
    }, 5000);
  };

  const retryScrapingHandler = async () => {
    if (!result?.shipment_id) return;
    try {
      setResult(prev => ({ ...prev, status: "Procesando", status_detail: "Reintentando generación de guía...", scraping_in_progress: true }));
      await axios.post(`${API}/shipments/${result.shipment_id}/retry-scraping`);
      toast.info("Reintentando generación de guía FedEx...");
      pollScrapingStatus(result.shipment_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al reintentar");
      setResult(prev => ({ ...prev, status: "Error", scraping_in_progress: false }));
    }
  };

  const logoUrl = CARRIER_LOGOS[quote.carrier];

  return (
    <div className="min-h-screen bg-zinc-100">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/dashboard")}>
            <Package className="w-7 h-7 text-violet-600" />
            <span className="text-xl font-bold font-['Outfit']">
              <span className="text-violet-600">Super</span><span className="text-zinc-800">Pack</span>
            </span>
          </div>
          <Badge variant="outline" className="text-violet-600 border-violet-300">
            Saldo: ${user?.balance?.toFixed(2)} MXN
          </Badge>
        </div>
      </header>

      {/* Step Indicator */}
      <div className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {STEPS.map((step, idx) => {
              const Icon = step.icon;
              const isActive = idx === currentStep;
              const isCompleted = idx < currentStep;
              return (
                <div key={step.id} className="flex items-center flex-1">
                  <div className="flex flex-col items-center flex-1">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                      isCompleted ? "bg-emerald-500 text-white" :
                      isActive ? "bg-violet-600 text-white shadow-lg shadow-violet-500/30" :
                      "bg-zinc-200 text-zinc-400"
                    }`}>
                      {isCompleted ? <CheckCircle className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                    </div>
                    <span className={`text-xs mt-1 font-medium hidden sm:block ${
                      isActive ? "text-violet-600" : isCompleted ? "text-emerald-600" : "text-zinc-400"
                    }`}>{step.label}</span>
                  </div>
                  {idx < STEPS.length - 1 && (
                    <div className={`h-0.5 flex-1 mx-1 ${isCompleted ? "bg-emerald-400" : "bg-zinc-200"}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Selected carrier summary */}
      {currentStep < 4 && (
        <div className="max-w-5xl mx-auto px-4 mt-6">
          <Card className="bg-violet-50 border-violet-200">
            <CardContent className="py-3 px-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {logoUrl && <img src={logoUrl} alt={quote.carrier} className="h-8 object-contain" />}
                <div>
                  <span className="font-bold">{quote.carrier}</span>
                  <Badge className="ml-2 bg-violet-100 text-violet-700 text-xs">{quote.service_type}</Badge>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xl font-bold text-violet-700">${quote.price.toFixed(2)} MXN</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Form Content */}
      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Step 0: Remitente */}
        {currentStep === 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-violet-600" />
                Datos del remitente (quien envía)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <AddressForm data={sender} setData={setSender} prefix="sender" />
            </CardContent>
          </Card>
        )}

        {/* Step 1: Destinatario */}
        {currentStep === 1 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-violet-600" />
                Datos del destinatario (quien recibe)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <AddressForm data={recipient} setData={setRecipient} prefix="recipient" />
            </CardContent>
          </Card>
        )}

        {/* Step 2: Paquete */}
        {currentStep === 2 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Box className="w-5 h-5 text-violet-600" />
                Contenido y dimensiones del paquete
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label>Descripción del contenido *</Label>
                <Textarea
                  placeholder="Ej: Ropa, electrónicos, documentos, artesanías..."
                  value={pkg.content_description}
                  onChange={(e) => setPkg({...pkg, content_description: e.target.value})}
                  required rows={3} className="mt-1"
                  data-testid="package-content-input"
                />
              </div>

              {/* Código SAT */}
              <div>
                <Label className="flex items-center gap-2">
                  <Search className="w-4 h-4 text-violet-500" />
                  Código SAT del producto
                </Label>
                <SATCodeSearch
                  value={pkg.codigo_sat}
                  desc={pkg.codigo_sat_desc}
                  onChange={(code, desc) => setPkg({...pkg, codigo_sat: code, codigo_sat_desc: desc})}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Valor declarado (MXN)</Label>
                  <Input type="number" placeholder="0.00" min="0" step="0.01"
                    value={pkg.declared_value}
                    onChange={(e) => setPkg({...pkg, declared_value: e.target.value})}
                    className="mt-1" data-testid="package-value-input"
                  />
                </div>
                <div>
                  <Label>Peso (kg) *</Label>
                  <Input type="number" placeholder="2.5" min="0.1" step="0.1"
                    value={pkg.weight}
                    onChange={(e) => setPkg({...pkg, weight: e.target.value})}
                    required className="mt-1" data-testid="package-weight-input"
                  />
                </div>
              </div>
              <div>
                <Label>Dimensiones (cm) *</Label>
                <div className="grid grid-cols-3 gap-3 mt-1">
                  <Input type="number" placeholder="Largo" min="1"
                    value={pkg.length}
                    onChange={(e) => setPkg({...pkg, length: e.target.value})}
                    required data-testid="package-length-input"
                  />
                  <Input type="number" placeholder="Ancho" min="1"
                    value={pkg.width}
                    onChange={(e) => setPkg({...pkg, width: e.target.value})}
                    required data-testid="package-width-input"
                  />
                  <Input type="number" placeholder="Alto" min="1"
                    value={pkg.height}
                    onChange={(e) => setPkg({...pkg, height: e.target.value})}
                    required data-testid="package-height-input"
                  />
                </div>
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={pkg.is_fragile}
                    onChange={(e) => setPkg({...pkg, is_fragile: e.target.checked})}
                    className="w-4 h-4 rounded border-zinc-300 text-violet-600 focus:ring-violet-500"
                    data-testid="package-fragile-check"
                  />
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  <span className="text-sm font-medium">Contenido frágil</span>
                </label>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Pago */}
        {currentStep === 3 && (
          <div className="space-y-6">
            {/* Resumen */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-violet-600" />
                  Resumen del envío
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-6">
                  <SummaryBlock title="Remitente" icon={<User className="w-4 h-4" />} data={sender} />
                  <SummaryBlock title="Destinatario" icon={<MapPin className="w-4 h-4" />} data={recipient} />
                </div>
                <div className="bg-zinc-50 rounded-lg p-4">
                  <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                    <Box className="w-4 h-4 text-violet-600" /> Paquete
                  </h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <p><span className="text-zinc-500">Contenido:</span> {pkg.content_description}</p>
                    <p><span className="text-zinc-500">Peso:</span> {pkg.weight} kg</p>
                    <p><span className="text-zinc-500">Dimensiones:</span> {pkg.length}x{pkg.width}x{pkg.height} cm</p>
                    <p><span className="text-zinc-500">Valor declarado:</span> ${pkg.declared_value || "0"} MXN</p>
                    {pkg.codigo_sat && <p><span className="text-zinc-500">Código SAT:</span> {pkg.codigo_sat} - {pkg.codigo_sat_desc}</p>}
                    {pkg.is_fragile && <Badge className="bg-amber-100 text-amber-700 w-fit">Frágil</Badge>}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pago - Solo con saldo */}
            <Card className="border-violet-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-violet-600" />
                  Pago con saldo
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Carrier + Price */}
                <div className="p-4 bg-zinc-50 rounded-lg space-y-3">
                  <div className="flex items-center gap-3">
                    {logoUrl && <img src={logoUrl} alt={quote.carrier} className="h-10 object-contain" />}
                    <div>
                      <p className="font-bold text-lg">{quote.carrier} - {quote.service_type}</p>
                      <p className="text-sm text-zinc-500">{quote.delivery_days}</p>
                    </div>
                  </div>
                  <div className="border-t border-zinc-200 pt-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-zinc-500">Precio SuperPack</span>
                      <span className="text-2xl font-bold text-violet-700">${quote.price.toFixed(2)} MXN</span>
                    </div>
                  </div>
                </div>

                {/* PUDO warning for PuntoPost */}
                {(quote.carrier === "PUNTOPOST" || quote.etp_service_code?.includes("PUDO")) && (
                  <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4" data-testid="pudo-warning">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                      <div>
                        <p className="font-bold text-amber-800 text-sm">Servicio punto a punto (PUDO)</p>
                        <ul className="text-xs text-amber-700 mt-1.5 space-y-1">
                          <li>1. Tú llevas tu paquete a un punto PuntoPost cercano a tu dirección</li>
                          <li>2. El destinatario lo recoge en un punto PuntoPost cercano a su dirección</li>
                          <li>3. <strong>No hay entrega ni recolección a domicilio</strong></li>
                          <li>4. Los puntos específicos se asignan al generar la guía</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* Balance info */}
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <span className="text-zinc-600">Tu saldo actual</span>
                  <span className={`text-xl font-bold ${user.balance >= quote.price ? "text-emerald-600" : "text-red-600"}`}>
                    ${user?.balance?.toFixed(2)} MXN
                  </span>
                </div>

                {user.balance >= quote.price ? (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                    <p className="text-emerald-700 font-medium flex items-center gap-2">
                      <Shield className="w-5 h-5" />
                      Saldo suficiente
                    </p>
                    <p className="text-emerald-600 text-sm mt-1">
                      Saldo restante después del pago: <strong>${(user.balance - quote.price).toFixed(2)} MXN</strong>
                    </p>
                  </div>
                ) : (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-700 font-medium flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" />
                      Saldo insuficiente
                    </p>
                    <p className="text-red-600 text-sm mt-1">
                      Te faltan <strong>${(quote.price - user.balance).toFixed(2)} MXN</strong>. Recarga tu saldo para continuar.
                    </p>
                    <p className="text-red-500 text-xs mt-1">Recarga mínima: $100 MXN por SPEI (sin comisión)</p>
                    <Button
                      className="mt-3 bg-violet-600 hover:bg-violet-700"
                      onClick={() => navigate("/dashboard/recargar")}
                      data-testid="recharge-from-flow-btn"
                    >
                      <DollarSign className="w-4 h-4 mr-2" /> Recargar saldo
                    </Button>
                  </div>
                )}

                {/* Confirm button */}
                <Button
                  onClick={handlePayment}
                  disabled={loading || user.balance < quote.price}
                  className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 py-7 text-lg font-bold shadow-xl shadow-violet-500/30"
                  data-testid="confirm-payment-btn"
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Procesando pago...
                    </div>
                  ) : (
                    <>Confirmar y pagar ${quote.price.toFixed(2)} MXN</>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 4: Éxito */}
        {currentStep === 4 && result && (
          <Card className={result?.scraping_in_progress ? "border-violet-300" : result?.status === "Error" ? "border-red-300" : "border-emerald-300"}>
            <CardContent className="py-12 text-center space-y-6">
              <div className={`w-24 h-24 ${result?.scraping_in_progress ? "bg-violet-100" : result?.status === "Error" ? "bg-red-100" : "bg-emerald-100"} rounded-full flex items-center justify-center mx-auto`}>
                {result?.scraping_in_progress ? (
                  <Truck className="w-14 h-14 text-violet-600 animate-pulse" />
                ) : result?.status === "Error" ? (
                  <AlertTriangle className="w-14 h-14 text-red-500" />
                ) : (
                  <CheckCircle className="w-14 h-14 text-emerald-600" />
                )}
              </div>
              <div>
                {result?.scraping_in_progress ? (
                  <>
                    <h2 className="text-3xl font-bold font-['Outfit'] text-zinc-900">Procesando tu envío...</h2>
                    <p className="text-zinc-500 mt-2">Una vez generada, podrás ver tu guía en la sección de <strong>Mis Envíos</strong>.</p>
                    <div className="mt-4 flex items-center justify-center gap-2">
                      <div className="w-2 h-2 bg-violet-500 rounded-full animate-bounce" style={{animationDelay: "0ms"}} />
                      <div className="w-2 h-2 bg-violet-500 rounded-full animate-bounce" style={{animationDelay: "150ms"}} />
                      <div className="w-2 h-2 bg-violet-500 rounded-full animate-bounce" style={{animationDelay: "300ms"}} />
                    </div>
                  </>
                ) : result?.status === "Error" ? (
                  <>
                    <h2 className="text-3xl font-bold font-['Outfit'] text-red-700">Error al generar guía</h2>
                    <p className="text-zinc-500 mt-2">{result?.status_detail || "Hubo un problema al generar la guía. Contacta soporte."}</p>
                    {result?.scraping_method === "fedex_playwright" || result?.shipment_id ? (
                      <Button onClick={retryScrapingHandler} className="mt-3 bg-violet-600 hover:bg-violet-700" data-testid="retry-scraping-btn">
                        <RefreshCw className="w-4 h-4 mr-2" /> Reintentar generación
                      </Button>
                    ) : null}
                  </>
                ) : (
                  <>
                    <h2 className="text-3xl font-bold font-['Outfit'] text-zinc-900">¡Guía generada!</h2>
                    <p className="text-zinc-500 mt-2">Tu envío ha sido creado exitosamente</p>
                  </>
                )}
              </div>

              <div className="bg-zinc-50 rounded-xl p-6 max-w-md mx-auto space-y-4">
                {result.tracking_number ? (
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-500">Número de guía</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-lg text-violet-700" data-testid="tracking-number">
                        {result.tracking_number}
                      </span>
                      <Button size="sm" variant="ghost" onClick={() => {
                        navigator.clipboard.writeText(result.tracking_number);
                        toast.success("Número copiado");
                      }}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ) : result?.scraping_in_progress ? (
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-500">Número de guía</span>
                    <span className="text-violet-500 text-sm animate-pulse">Generando...</span>
                  </div>
                ) : null}
                <div className="flex items-center justify-between">
                  <span className="text-zinc-500">Paquetería</span>
                  <div className="flex items-center gap-2">
                    {logoUrl && <img src={logoUrl} alt={result.carrier} className="h-6 object-contain" />}
                    <span className="font-medium">{result.carrier} {result.service_type}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-zinc-500">Total pagado</span>
                  <span className="font-bold text-emerald-600">${result.price?.toFixed(2)} MXN</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-zinc-500">Método de pago</span>
                  <span className="font-medium">
                    {result.payment_method === "card" ? "Tarjeta" : "Saldo"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-zinc-500">Estado</span>
                  <Badge className="bg-emerald-100 text-emerald-700">{result.status}</Badge>
                </div>
                {result.label_url && (
                  <div className="pt-2 border-t border-zinc-200">
                    <a href={result.label_url} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-violet-600 hover:text-violet-800 font-medium text-sm"
                      data-testid="download-label-btn">
                      <FileText className="w-4 h-4" />
                      Descargar etiqueta / guía PDF
                    </a>
                  </div>
                )}
              </div>

              <div className="flex gap-4 justify-center pt-4">
                <Button variant="outline" onClick={() => navigate("/dashboard/historial")}
                  data-testid="go-to-history-btn">
                  Ver mis envíos
                </Button>
                <Button onClick={() => navigate("/dashboard")}
                  className="bg-violet-600 hover:bg-violet-700" data-testid="new-shipment-btn">
                  Nuevo envío
                </Button>
              </div>

              {/* Save addresses prompt */}
              <div className="border-t border-zinc-200 pt-6 max-w-md mx-auto">
                <p className="text-sm text-zinc-500 mb-3">¿Guardar estas direcciones para futuros envíos?</p>
                <div className="flex gap-2 justify-center">
                  <Button size="sm" variant="outline" onClick={async () => {
                    try {
                      await axios.post(`${API}/addresses`, {
                        label: `Remitente - ${sender.name}`, address_type: "remitente",
                        name: sender.name, company: sender.company, street: sender.street,
                        colonia: sender.colonia, city: sender.city, state: sender.state,
                        zip: sender.zip, phone: sender.phone, email: sender.email,
                      });
                      toast.success("Dirección de remitente guardada");
                    } catch { toast.error("Error al guardar"); }
                  }} data-testid="save-sender-btn">
                    <BookOpen className="w-3.5 h-3.5 mr-1" /> Guardar remitente
                  </Button>
                  <Button size="sm" variant="outline" onClick={async () => {
                    try {
                      await axios.post(`${API}/addresses`, {
                        label: `Destinatario - ${recipient.name}`, address_type: "destinatario",
                        name: recipient.name, company: recipient.company, street: recipient.street,
                        colonia: recipient.colonia, city: recipient.city, state: recipient.state,
                        zip: recipient.zip, phone: recipient.phone, email: recipient.email,
                      });
                      toast.success("Dirección de destinatario guardada");
                    } catch { toast.error("Error al guardar"); }
                  }} data-testid="save-recipient-btn">
                    <BookOpen className="w-3.5 h-3.5 mr-1" /> Guardar destinatario
                  </Button>
                </div>
              </div>

              {/* Tracking Timeline */}
              {result.shipment_id && (
                <div className="max-w-md mx-auto mt-4">
                  <TrackingTimeline shipmentId={result.shipment_id} />
                </div>
              )}

              {/* Carrier Instructions */}
              <CarrierInstructions carrier={result.carrier || quote.carrier} />
            </CardContent>
          </Card>
        )}

        {/* Navigation Buttons */}
        {currentStep < 3 && (
          <div className="flex justify-between mt-6">
            <Button variant="outline" onClick={currentStep === 0 ? () => navigate(-1) : prevStep}
              data-testid="flow-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {currentStep === 0 ? "Volver" : "Anterior"}
            </Button>
            <Button onClick={nextStep} className="bg-violet-600 hover:bg-violet-700"
              data-testid="flow-next-btn">
              Siguiente <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}
        {currentStep === 3 && (
          <div className="flex justify-start mt-6">
            <Button variant="outline" onClick={prevStep} data-testid="flow-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Anterior
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// SAT Code Searchable Dropdown
const SATCodeSearch = ({ value, desc, onChange }) => {
  const [search, setSearch] = useState(value ? `${value} - ${desc}` : "");
  const [open, setOpen] = useState(false);
  const [filtered, setFiltered] = useState([]);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearch = (val) => {
    setSearch(val);
    setOpen(true);
    if (val.length < 2) {
      setFiltered(CODIGOS_SAT.slice(0, 10));
      return;
    }
    const q = val.toLowerCase();
    setFiltered(CODIGOS_SAT.filter(h =>
      h.code.includes(q) || h.desc.toLowerCase().includes(q)
    ).slice(0, 15));
  };

  const handleSelect = (item) => {
    setSearch(`${item.code} - ${item.desc}`);
    onChange(item.code, item.desc);
    setOpen(false);
  };

  return (
    <div className="relative mt-1" ref={wrapperRef}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
        <Input
          placeholder="Busca por código SAT o descripción..."
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={() => { handleSearch(search); setOpen(true); }}
          className="pl-10"
          data-testid="sat-code-search"
        />
      </div>
      {open && filtered.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-zinc-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
          {filtered.map((item) => (
            <div
              key={item.code}
              className="px-4 py-3 hover:bg-violet-50 cursor-pointer border-b border-zinc-50 last:border-0"
              onClick={() => handleSelect(item)}
              data-testid={`sat-option-${item.code}`}
            >
              <span className="font-mono text-violet-600 font-bold text-sm">{item.code}</span>
              <span className="ml-2 text-sm text-zinc-700">{item.desc}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Reusable address form with ZIP code lookup
const AddressForm = ({ data, setData, prefix }) => {
  const [colonias, setColonias] = useState([]);
  const hasFetchedRef = useRef(false);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [showColoniaSearch, setShowColoniaSearch] = useState(false);
  const [manualColonia, setManualColonia] = useState(false);
  const update = (field, value) => setData(prev => ({ ...prev, [field]: value }));

  const fetchColonias = async (val) => {
    if (val.length === 5) {
      try {
        const res = await axios.get(`${API}/zipcode/${val}`);
        if (res.data.colonias?.length > 0) {
          setColonias(res.data.colonias);
          if (res.data.estado) update("state", res.data.estado);
          if (res.data.ciudad) update("city", res.data.ciudad);
          if (res.data.colonias.length === 1) {
            update("colonia", res.data.colonias[0]);
          }
        } else {
          setColonias([]);
        }
      } catch { setColonias([]); }
    } else {
      setColonias([]);
    }
  };

  const handleZipChange = async (val) => {
    update("zip", val);
    update("colonia", "");
    fetchColonias(val);
  };

  useEffect(() => {
    if (data.zip?.length === 5 && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchColonias(data.zip);
    }
    // Load saved addresses
    axios.get(`${API}/addresses`).then(res => {
      const type = prefix === "sender" ? "remitente" : "destinatario";
      const filtered = (res.data.addresses || []).filter(a => a.address_type === "both" || a.address_type === type);
      setSavedAddresses(filtered);
    }).catch(() => {});
  }, []);

  const loadAddress = (addr) => {
    setData(prev => ({
      ...prev,
      name: addr.name, company: addr.company || "", street: addr.street,
      colonia: addr.colonia, city: addr.city, state: addr.state,
      zip: addr.zip, phone: addr.phone, email: addr.email,
    }));
    if (addr.zip?.length === 5) fetchColonias(addr.zip);
    setShowSaved(false);
    toast.success(`Dirección "${addr.label}" cargada`);
  };

  return (
    <div className="space-y-4">
      {/* Saved addresses selector */}
      {savedAddresses.length > 0 && (
        <div className="bg-violet-50 border border-violet-200 rounded-lg p-3">
          <button type="button" onClick={() => setShowSaved(!showSaved)}
            className="flex items-center gap-2 text-sm font-medium text-violet-700 hover:text-violet-900 w-full"
            data-testid={`${prefix}-load-saved-btn`}
          >
            <BookOpen className="w-4 h-4" />
            Cargar dirección guardada ({savedAddresses.length})
            <span className="ml-auto text-xs">{showSaved ? "▲" : "▼"}</span>
          </button>
          {showSaved && (
            <div className="mt-3 space-y-2 max-h-48 overflow-y-auto">
              {savedAddresses.map(addr => (
                <button key={addr.address_id} type="button" onClick={() => loadAddress(addr)}
                  className="w-full text-left p-2.5 bg-white rounded-lg border border-violet-100 hover:border-violet-400 hover:bg-violet-50 transition-colors text-sm"
                  data-testid={`${prefix}-saved-addr-${addr.address_id}`}
                >
                  <span className="font-semibold text-violet-800">{addr.label}</span>
                  <span className="text-zinc-500 ml-2">{addr.name} — {addr.street}, {addr.colonia}, CP {addr.zip}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label>Nombre de contacto *</Label>
          <Input placeholder="Juan Pérez López" value={data.name}
            onChange={(e) => update("name", e.target.value)} required className="mt-1 h-12 text-base"
            data-testid={`${prefix}-name-input`}
          />
        </div>
        <div>
          <Label>Empresa (opcional)</Label>
          <Input placeholder="Mi Empresa S.A. de C.V." value={data.company}
            onChange={(e) => update("company", e.target.value)} className="mt-1 h-12 text-base"
            data-testid={`${prefix}-company-input`}
          />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label>Teléfono *</Label>
          <Input placeholder="55 1234 5678" value={data.phone}
            onChange={(e) => update("phone", e.target.value)} required className="mt-1 h-12 text-base"
            data-testid={`${prefix}-phone-input`}
          />
        </div>
        <div>
          <Label>Email *</Label>
          <Input type="email" placeholder="correo@ejemplo.com" value={data.email}
            onChange={(e) => update("email", e.target.value)} required className="mt-1 h-12 text-base"
            data-testid={`${prefix}-email-input`}
          />
        </div>
      </div>
      <div>
        <Label>Dirección línea 1 *</Label>
        <Input placeholder="Av. Insurgentes Sur 1234, Int. 5" value={data.street}
          onChange={(e) => update("street", e.target.value)} required className="mt-1 h-12 text-base"
          data-testid={`${prefix}-street-input`}
        />
      </div>
      {/* Dirección línea 2 (Colonia) - full width for mobile */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label>Dirección línea 2 (Colonia) *</Label>
          {colonias.length > 0 && !manualColonia ? (
            <>
              <select value={data.colonia} onChange={(e) => update("colonia", e.target.value)}
                className="w-full mt-1 h-12 px-3 border-2 border-violet-200 rounded-md text-base font-medium focus:outline-none focus:ring-2 focus:ring-violet-500 bg-violet-50 text-violet-700"
                required data-testid={`${prefix}-colonia-select`}
              >
                <option value="">Selecciona una colonia</option>
                {colonias.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <button type="button" onClick={() => { setManualColonia(true); update("colonia", ""); }}
                className="text-[11px] text-violet-600 hover:text-violet-800 mt-1 flex items-center gap-1 font-medium"
                data-testid={`${prefix}-manual-colonia-btn`}>
                ¿No encuentras tu colonia? Escríbela manualmente
              </button>
            </>
          ) : (
            <>
              <Input placeholder="Escribe tu colonia" value={data.colonia}
                onChange={(e) => update("colonia", e.target.value)} required className="mt-1 h-12 text-base"
                data-testid={`${prefix}-colonia-input`}
              />
              {colonias.length > 0 && manualColonia && (
                <button type="button" onClick={() => { setManualColonia(false); update("colonia", ""); }}
                  className="text-[11px] text-violet-600 hover:text-violet-800 mt-1 flex items-center gap-1 font-medium"
                  data-testid={`${prefix}-dropdown-colonia-btn`}>
                  Volver a seleccionar del listado
                </button>
              )}
            </>
          )}
        </div>
        <div>
          <Label>Dirección línea 3 (opcional)</Label>
          <Input placeholder="Referencia, edificio, etc." value={data.address_line3 || ""}
            onChange={(e) => update("address_line3", e.target.value)} className="mt-1 h-12 text-base"
            data-testid={`${prefix}-address3-input`}
          />
        </div>
      </div>
      {/* CP primero para auto-rellenar */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label>Código Postal *</Label>
          {showColoniaSearch ? (
            <div className="mt-1">
              <ColoniaSearch
                placeholder="Busca tu colonia..."
                onSelect={(item) => {
                  update("zip", item.cp);
                  update("colonia", item.colonia);
                  update("state", item.estado);
                  update("city", item.ciudad || item.municipio);
                  fetchColonias(item.cp);
                  setShowColoniaSearch(false);
                  toast.success(`CP ${item.cp} - ${item.colonia} seleccionado`);
                }}
                onClose={() => setShowColoniaSearch(false)}
              />
            </div>
          ) : (
            <>
              <Input placeholder="06600" maxLength={5} value={data.zip}
                onChange={(e) => handleZipChange(e.target.value)} required className="mt-1 h-12 text-base"
                data-testid={`${prefix}-zip-input`}
              />
              <div className="flex items-center justify-between mt-1">
                {colonias.length > 0 && (
                  <p className="text-xs text-violet-600">{colonias.length} colonia(s) encontrada(s)</p>
                )}
                <button type="button" onClick={() => setShowColoniaSearch(true)}
                  className="text-[11px] text-violet-600 hover:text-violet-800 flex items-center gap-1 font-medium ml-auto"
                  data-testid={`${prefix}-search-colonia-btn`}>
                  <Search className="w-3 h-3" /> ¿No sabes tu CP?
                </button>
              </div>
            </>
          )}
        </div>
        <div>
          <Label>Estado / Provincia *</Label>
          <select value={data.state} onChange={(e) => update("state", e.target.value)}
            className="w-full mt-1 h-12 px-3 border border-zinc-200 rounded-md text-base focus:outline-none focus:ring-2 focus:ring-violet-500"
            required data-testid={`${prefix}-state-select`}
          >
            <option value="">Selecciona un estado</option>
            {ESTADOS_MEXICO.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>
      <div>
        <Label>Ciudad *</Label>
        <Input placeholder="Ciudad de México" value={data.city}
          onChange={(e) => update("city", e.target.value)} required className="mt-1 h-12 text-base"
          data-testid={`${prefix}-city-input`}
        />
      </div>
      {/* No extra references field - already covered by Address line 3 */}
    </div>
  );
};

// Summary block for review step
const SummaryBlock = ({ title, icon, data }) => (
  <div className="bg-zinc-50 rounded-lg p-4">
    <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
      <span className="text-violet-600">{icon}</span> {title}
    </h4>
    <div className="text-sm space-y-1">
      <p className="font-medium">{data.name}</p>
      {data.company && <p className="text-zinc-500">{data.company}</p>}
      <p><span className="text-zinc-400">Línea 1:</span> {data.street}</p>
      <p><span className="text-zinc-400">Línea 2:</span> {data.colonia}</p>
      {data.address_line3 && <p><span className="text-zinc-400">Línea 3:</span> {data.address_line3}</p>}
      <p>{data.city}, {data.state} CP {data.zip}</p>
      <p className="flex items-center gap-1"><Phone className="w-3 h-3" /> {data.phone}</p>
      <p className="flex items-center gap-1"><Mail className="w-3 h-3" /> {data.email}</p>
    </div>
  </div>
);

// Carrier drop-off and pickup instructions
const CARRIER_INSTRUCTIONS = {
  "PUNTOPOST": {
    dropoff: "Lleva tu paquete con la guía impresa al punto PuntoPost más cercano a tu ubicación. Consulta puntos disponibles en puntopost.com o en tu guía PDF. Se te asignará un punto específico al generar la guía.",
    pickup: "El destinatario recibirá una notificación cuando el paquete llegue al punto PuntoPost cercano a su ubicación. Deberá presentar una identificación para recogerlo.",
    tip: "PuntoPost es un servicio punto a punto (PUDO). NO hay entrega ni recolección a domicilio. Tú dejas el paquete en un punto y el destinatario lo recoge en otro punto."
  },
  "FedEx": {
    dropoff: "Lleva tu paquete a cualquier sucursal FedEx o centro autorizado FedEx Ship Center. Localiza la sucursal más cercana en fedex.com/locate. Presenta tu guía impresa o en pantalla.",
    pickup: "Programa una recolección en fedex.com o llamando al 800-900-1100. La recolección tiene un costo adicional. Horario: Lunes a Viernes de 9:00 a 18:00.",
    tip: "Empaca bien tu paquete. FedEx ofrece material de empaque en sus sucursales."
  },
  "DHL": {
    dropoff: "Deposita tu paquete en cualquier sucursal DHL Express o punto ServicePoint. Consulta ubicaciones en dhl.com.mx. Presenta tu guía impresa.",
    pickup: "Solicita recolección a domicilio en dhl.com.mx o al 800-765-6345. Disponible en la mayoría de las ciudades.",
    tip: "DHL recomienda empacar con al menos 5 cm de material de protección."
  },
  "Estafeta": {
    dropoff: "Entrega tu paquete en cualquier sucursal Estafeta. Consulta la más cercana en estafeta.com/herramientas/centro-de-envio. Lleva la guía impresa.",
    pickup: "Contacta al 800-378-2338 para programar una recolección. Disponible en ciudades con cobertura Estafeta.",
    tip: "Estafeta acepta paquetes de hasta 70 kg en servicio terrestre."
  },
  "UPS": {
    dropoff: "Lleva tu paquete a un UPS Access Point o sucursal UPS. Localízalos en ups.com/dropoff. Muestra tu guía impresa o digital.",
    pickup: "Programa recolección en ups.com o al 800-742-5877. Disponible con costo adicional.",
    tip: "UPS tiene puntos de entrega en tiendas de conveniencia participantes."
  },
  "Redpack": {
    dropoff: "Lleva tu paquete a cualquier sucursal Redpack. Consulta ubicaciones en redpack.com.mx. Presenta tu guía impresa.",
    pickup: "Solicita recolección llamando al 800-726-7225 o en redpack.com.mx.",
    tip: "Redpack ofrece servicio exprés y terrestre a nivel nacional."
  },
  "J&T Express": {
    dropoff: "Deposita tu paquete en cualquier punto J&T Express. Consulta ubicaciones en jtexpress.mx. Presenta tu guía impresa o código QR.",
    pickup: "Solicita recolección gratuita desde la app o web de J&T Express.",
    tip: "J&T Express ofrece recolección gratuita en la mayoría de las ciudades."
  },
  "Paquetexpress": {
    dropoff: "Entrega tu paquete en una sucursal Paquetexpress. Consulta la red de sucursales en paquetexpress.com.mx. Lleva tu guía impresa.",
    pickup: "Contacta al 800-702-0202 para solicitar recolección a domicilio.",
    tip: "Paquetexpress es ideal para envíos pesados y de gran volumen."
  },
  "99 Minutos": {
    dropoff: "99 Minutos funciona principalmente con recolección a domicilio. Consulta puntos de entrega disponibles en tu zona en 99minutos.com.",
    pickup: "La recolección a domicilio es el método estándar. Se programa automáticamente al generar la guía.",
    tip: "Ideal para envíos dentro de la misma ciudad con entrega el mismo día."
  },
};

const CarrierInstructions = ({ carrier }) => {
  const [expanded, setExpanded] = useState(false);
  const instructions = CARRIER_INSTRUCTIONS[carrier];
  const generic = !instructions;

  return (
    <div className="border-t border-zinc-200 pt-6 max-w-lg mx-auto mt-2" data-testid="carrier-instructions">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between text-left group"
        data-testid="carrier-instructions-toggle"
      >
        <div className="flex items-center gap-2">
          <Truck className="w-5 h-5 text-violet-600" />
          <span className="font-semibold text-sm text-zinc-800">
            ¿Cómo entregar o recoger mi paquete?
          </span>
        </div>
        <span className="text-xs text-violet-600 group-hover:text-violet-800 font-medium">
          {expanded ? "Ocultar" : "Ver instrucciones"}
        </span>
      </button>

      {expanded && (
        <div className="mt-4 space-y-4 animate-fade-in text-left">
          <div className="bg-violet-50 rounded-lg p-4">
            <h4 className="font-bold text-sm text-violet-800 mb-2 flex items-center gap-2">
              <MapPin className="w-4 h-4" /> Opción 1: Dejar en sucursal
            </h4>
            <p className="text-sm text-zinc-700 leading-relaxed">
              {generic
                ? "Lleva tu paquete con la guía impresa a cualquier sucursal de la paquetería seleccionada. Consulta ubicaciones en el sitio web oficial de la paquetería."
                : instructions.dropoff}
            </p>
          </div>

          <div className="bg-emerald-50 rounded-lg p-4">
            <h4 className="font-bold text-sm text-emerald-800 mb-2 flex items-center gap-2">
              <Truck className="w-4 h-4" /> Opción 2: Solicitar recolección
            </h4>
            <p className="text-sm text-zinc-700 leading-relaxed">
              {generic
                ? "Contacta a la paquetería para programar una recolección a domicilio. Puede aplicar un cargo adicional."
                : instructions.pickup}
            </p>
          </div>

          {(instructions?.tip || generic) && (
            <div className="bg-amber-50 rounded-lg p-3">
              <p className="text-xs text-amber-800 flex items-start gap-2">
                <Shield className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>
                  <strong>Tip:</strong>{" "}
                  {generic
                    ? "Siempre imprime tu guía y pégala visible en el paquete. Empaca bien tu contenido para evitar daños durante el transporte."
                    : instructions.tip}
                </span>
              </p>
            </div>
          )}

          <div className="bg-zinc-50 rounded-lg p-3">
            <p className="text-xs text-zinc-600 leading-relaxed">
              <strong>Pasos generales:</strong><br/>
              1. Descarga e imprime tu guía PDF<br/>
              2. Pega la guía en el exterior del paquete<br/>
              3. Entrega en sucursal o solicita recolección<br/>
              4. Conserva tu número de guía para rastreo
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShippingFlow;
